import { Button, Spinner, Text } from '@/ui-kit'
import { zodResolver } from '@hookform/resolvers/zod'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod/v3'
import { FormSelect } from '../missions/components/FormSelect'
import { FormSelectDropdown } from '../missions/components/FormSelectDropdown'
import { toSelectOptions, useCountries, useLanguages } from './api/dictionaries'
import { LanguagePickerModal } from './LanguagePickerModal'

/** Sentinel value that triggers the "Other" language picker modal. Never stored in the form. */
const OTHER_LANGUAGE_SENTINEL = '__other__'

const AGE_BRACKET_OPTIONS: { value: string; label: MessageDescriptor | string }[] = [
  { value: '18-', label: msg`Under 18` },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25–34' },
  { value: '35-44', label: '35–44' },
  { value: '45-54', label: '45–54' },
  { value: '55-64', label: '55–64' },
  { value: '65+', label: '65+' }
]

const GENDER_OPTIONS: { value: string; label: MessageDescriptor }[] = [
  { value: 'male', label: msg`Male` },
  { value: 'female', label: msg`Female` },
  { value: 'other', label: msg`Other` }
]

const RACE_ETHNICITY_OPTIONS: { value: string; label: MessageDescriptor }[] = [
  { value: 'white', label: msg`White` },
  { value: 'asian', label: msg`Asian` },
  { value: 'middle-eastern', label: msg`Middle Eastern` },
  { value: 'hispanic-latino', label: msg`Hispanic or Latin` },
  { value: 'black-african', label: msg`Black or African Descent` },
  { value: 'multiracial', label: msg`Multiracial` },
  { value: 'other', label: msg`Other` }
]

const LANGUAGE_LEVEL_OPTIONS: { value: string; label: MessageDescriptor }[] = [
  { value: 'A1', label: msg`A1 - Beginner` },
  { value: 'A2', label: msg`A2 - Elementary` },
  { value: 'B1', label: msg`B1 - Intermediate` },
  { value: 'B2', label: msg`B2 - Upper Intermediate` },
  { value: 'C1', label: msg`C1 - Advanced` },
  { value: 'C2', label: msg`C2 - Proficient` },
  { value: 'Native', label: msg`Native / Bilingual` }
]

type LangOption = { value: string; label: string }

/** Native language: hide codes already chosen as a secondary language (current selection stays visible). */
function filterNativeLanguageOptions(
  options: readonly LangOption[],
  nativeLanguage: string,
  secondaryLanguages: { language: string; level: string }[]
): LangOption[] {
  const usedInSecondary = new Set(
    secondaryLanguages.map((s) => s.language).filter((code): code is string => Boolean(code))
  )
  return options.filter((opt) => !usedInSecondary.has(opt.value) || opt.value === nativeLanguage)
}

/** Secondary row: hide native language and languages picked on other rows (this row's value stays visible). */
function filterSecondaryLanguageOptions(
  options: readonly LangOption[],
  nativeLanguage: string,
  secondaryLanguages: { language: string; level: string }[],
  rowIndex: number
): LangOption[] {
  const taken = new Set<string>()
  if (nativeLanguage) taken.add(nativeLanguage)
  secondaryLanguages.forEach((s, i) => {
    if (i !== rowIndex && s.language) taken.add(s.language)
  })
  const current = secondaryLanguages[rowIndex]?.language
  return options.filter((opt) => !taken.has(opt.value) || opt.value === current)
}

const isAllowedLanguageCode = (code: string, allowedCodes: Set<string>) => {
  if (!code) return false
  if (!allowedCodes.size) return true
  return allowedCodes.has(code)
}

function createProfileSchema(languageCodes: readonly string[], t: (descriptor: MessageDescriptor) => string) {
  const allowedLanguageCodes = new Set(languageCodes)

  return z.object({
    ageBracket: z.string().min(1, t(msg`Select age bracket`)),
    gender: z.string().min(1, t(msg`Select gender`)),
    raceEthnicity: z.string().min(1, t(msg`Select race/ethnicity`)),
    countryOfOriginCode: z.string().min(1, t(msg`Select country of origin`)),
    countryOfResidenceCode: z.string().min(1, t(msg`Select country of residence`)),
    nativeLanguage: z
      .string()
      .min(1, t(msg`Select native language`))
      .refine((code) => isAllowedLanguageCode(code, allowedLanguageCodes), t(msg`Select language from the list`)),
    dialectCountry: z.string().optional(),
    secondaryLanguages: z
      .array(
        z.object({
          language: z
            .string()
            .min(1, t(msg`Select language`))
            .refine((code) => isAllowedLanguageCode(code, allowedLanguageCodes), t(msg`Select language from the list`)),
          level: z.string().min(1, t(msg`Select language level`))
        })
      )
      .max(5, t(msg`Maximum 5 secondary languages`))
  })
}

export type ProfileDetailsFormValues = z.infer<ReturnType<typeof createProfileSchema>>

type ProfileDetailsFormProps = {
  onSubmit: (data: ProfileDetailsFormValues) => void
  isSubmittingProfile: boolean
  defaultValues?: Partial<ProfileDetailsFormValues>
  className?: string
  submitButtonText?: string
  captchaSlot?: ReactNode
  submitDisabled?: boolean
}

/** Which language field triggered the picker modal: 'native' or a secondary-language row index. */
type LangPickerTarget = 'native' | number | null

export const ProfileDetailsForm = ({
  onSubmit,
  isSubmittingProfile,
  defaultValues,
  className,
  submitButtonText,
  captchaSlot,
  submitDisabled = false
}: ProfileDetailsFormProps) => {
  const { t } = useLingui()
  const { options: countryOptions } = useCountries()
  const { options: languageOptions } = useLanguages()

  const availableLanguages = useMemo(
    () => toSelectOptions(languageOptions.filter((language) => language.iso1 !== null)),
    [languageOptions]
  )

  const allLanguageOptions = useMemo(() => toSelectOptions(languageOptions), [languageOptions])

  const languageCodes = useMemo(() => allLanguageOptions.map((l) => l.value), [allLanguageOptions])
  const missionDemographicsSchema = useMemo(() => createProfileSchema(languageCodes, t), [languageCodes, t])

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<ProfileDetailsFormValues>({
    resolver: zodResolver(missionDemographicsSchema),
    defaultValues: {
      ageBracket: '',
      gender: '',
      raceEthnicity: '',
      countryOfOriginCode: '',
      countryOfResidenceCode: '',
      nativeLanguage: '',
      secondaryLanguages: [],
      ...defaultValues
    }
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'secondaryLanguages' })

  const nativeLanguage = watch('nativeLanguage')
  const secondaryLanguages = watch('secondaryLanguages') ?? []

  const otherOption = useMemo(() => ({ value: OTHER_LANGUAGE_SENTINEL, label: t`Other` }), [t])

  const nativeLanguageOptions = useMemo(
    () => [...filterNativeLanguageOptions(availableLanguages, nativeLanguage, secondaryLanguages), otherOption],
    [availableLanguages, nativeLanguage, secondaryLanguages, otherOption]
  )

  const [langPickerTarget, setLangPickerTarget] = useState<LangPickerTarget>(null)

  /** Options available in the modal for the current target — excludes already-used languages. */
  const langPickerOptions = useMemo(() => {
    if (langPickerTarget === null) return allLanguageOptions
    const taken = new Set<string>()
    if (langPickerTarget === 'native') {
      secondaryLanguages.forEach((s) => { if (s.language) taken.add(s.language) })
    } else {
      if (nativeLanguage) taken.add(nativeLanguage)
      secondaryLanguages.forEach((s, i) => { if (i !== langPickerTarget && s.language) taken.add(s.language) })
    }
    // Keep the current value visible so it can be re-selected (stays highlighted).
    const currentValue = langPickerTarget === 'native'
      ? nativeLanguage
      : secondaryLanguages[langPickerTarget]?.language
    return allLanguageOptions.filter((opt) => !taken.has(opt.value) || opt.value === currentValue)
  }, [langPickerTarget, allLanguageOptions, nativeLanguage, secondaryLanguages])

  const updateField = (name: keyof ProfileDetailsFormValues, value: string) => {
    setValue(name, value, { shouldValidate: true })
  }

  const openLangPicker = (target: LangPickerTarget) => setLangPickerTarget(target)
  const closeLangPicker = () => setLangPickerTarget(null)

  const handleNativeLanguageChange = (v: string) => {
    if (v === OTHER_LANGUAGE_SENTINEL) {
      openLangPicker('native')
    } else {
      updateField('nativeLanguage', v)
    }
  }

  const handleSecondaryLanguageChange = (index: number, v: string) => {
    if (v === OTHER_LANGUAGE_SENTINEL) {
      openLangPicker(index)
    } else {
      setValue(`secondaryLanguages.${index}.language`, v, { shouldValidate: true })
    }
  }

  const handleLangPickerSelect = (value: string) => {
    if (langPickerTarget === 'native') {
      updateField('nativeLanguage', value)
    } else if (typeof langPickerTarget === 'number') {
      setValue(`secondaryLanguages.${langPickerTarget}.language`, value, { shouldValidate: true })
    }
    closeLangPicker()
  }

  const langPickerCurrentValue =
    langPickerTarget === 'native'
      ? nativeLanguage
      : typeof langPickerTarget === 'number'
        ? secondaryLanguages[langPickerTarget]?.language
        : undefined

  const getLangLabel = (code: string) => languageOptions.find((l) => l.code === code)?.name

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={clsx('flex flex-col gap-6', className)}>
        <div className="flex flex-col gap-6">
          <Text type="subtitle-semibold" className="text-gray-70">
            <Trans>Demographics</Trans>
          </Text>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormSelect
              value={watch('ageBracket')}
              onChange={(v) => updateField('ageBracket', v)}
              label={t`Age bracket`}
              options={AGE_BRACKET_OPTIONS.map((o) => ({
                value: o.value,
                label: typeof o.label === 'string' ? o.label : t(o.label)
              }))}
              placeholder={t`Select age bracket`}
              errorMessage={errors.ageBracket?.message}
            />
            <FormSelect
              value={watch('gender')}
              onChange={(v) => updateField('gender', v)}
              label={t`Gender`}
              options={GENDER_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
              placeholder={t`Select gender`}
              errorMessage={errors.gender?.message}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormSelect
              value={watch('raceEthnicity')}
              onChange={(v) => updateField('raceEthnicity', v)}
              label={t`Race/ethnicity`}
              options={RACE_ETHNICITY_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
              placeholder={t`Select race/ethnicity`}
              errorMessage={errors.raceEthnicity?.message}
            />
            <FormSelectDropdown
              value={watch('countryOfResidenceCode')}
              onChange={(v) => updateField('countryOfResidenceCode', v)}
              label={t`Country of residence`}
              options={countryOptions}
              placeholder={t`Select country`}
              errorMessage={errors.countryOfResidenceCode?.message}
              searchable
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-solid border-gray-100 pt-6">
          <div className="mb-2 flex flex-col gap-2">
            <Text type="subtitle-semibold" className="text-gray-70">
              <Trans>Languages</Trans>
            </Text>
            <div className="text-body-2 text-gray-90 flex flex-col gap-4">
              <Trans>
                Your primary language is the one you use most often and feel most comfortable with for reading and
                communication. Your secondary language is an additional language you understand or use occasionally, but
                not as your main preference.
              </Trans>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormSelectDropdown
              value={watch('nativeLanguage')}
              valueLabel={getLangLabel(nativeLanguage)}
              onChange={handleNativeLanguageChange}
              label={t`Native language`}
              options={nativeLanguageOptions}
              placeholder={t`Select language`}
              errorMessage={errors.nativeLanguage?.message}
              searchable
            />

            <FormSelectDropdown
              value={watch('countryOfOriginCode')}
              onChange={(v) => updateField('countryOfOriginCode', v)}
              label={t`Country(Dialect)`}
              options={countryOptions}
              placeholder={t`Select country`}
              errorMessage={errors.countryOfOriginCode?.message}
              searchable
            />
          </div>
          <div className="mt-8 flex flex-col gap-6">
            {fields.map((field, index) => (
              <div key={field.id} className="flex flex-wrap items-start justify-center gap-4">
                <div className="max-mobile:max-w-full max-w-1/2 min-w-[300px] flex-1">
                  <FormSelectDropdown
                    value={watch(`secondaryLanguages.${index}.language`)}
                    valueLabel={getLangLabel(secondaryLanguages[index]?.language)}
                    onChange={(v) => handleSecondaryLanguageChange(index, v)}
                    label={t`Language`}
                    options={[
                      ...filterSecondaryLanguageOptions(
                        availableLanguages,
                        nativeLanguage,
                        secondaryLanguages,
                        index
                      ),
                      otherOption
                    ]}
                    placeholder={t`Select language`}
                    errorMessage={errors.secondaryLanguages?.[index]?.language?.message}
                    searchable
                  />
                </div>
                <div className="flex min-w-[300px] flex-1 gap-4">
                  <FormSelect
                    className="flex-1"
                    value={watch(`secondaryLanguages.${index}.level`)}
                    onChange={(v) =>
                      setValue(`secondaryLanguages.${index}.level`, v, {
                        shouldValidate: true
                      })
                    }
                    label={t`Language Level`}
                    options={LANGUAGE_LEVEL_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
                    placeholder={t`Select Language Level`}
                    errorMessage={errors.secondaryLanguages?.[index]?.level?.message}
                  />
                  <Button
                    type="button"
                    variant="bordered-gray"
                    onClick={() => remove(index)}
                    aria-label={t`Remove language`}
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </div>
            ))}
            {fields.length < 5 && (
              <Button
                type="button"
                variant="bordered-gray"
                size="small"
                onClick={() =>
                  append({
                    language: '',
                    level: 'A1'
                  })
                }
                className="max-mobile:w-full self-start"
              >
                <Plus size={16} className="mr-1.5 shrink-0" />
                <Trans>Add secondary language</Trans>
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-solid border-gray-100 pt-6">
          {captchaSlot}
          <Button
            type="submit"
            className="max-mobile:w-full max-mobile:max-w-full max-w-[300px]"
            isDisabled={isSubmittingProfile || submitDisabled}
          >
            {isSubmittingProfile && <Spinner size="small" />}
            {submitButtonText ?? t`Submit`}
          </Button>
        </div>
      </form>

      <LanguagePickerModal
        open={langPickerTarget !== null}
        onClose={closeLangPicker}
        onSelect={handleLangPickerSelect}
        options={langPickerOptions}
        currentValue={langPickerCurrentValue}
      />
    </>
  )
}
